import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const N = 8000;
const GOLD = new THREE.Color("#4D9FFF");

function samplePoint(segments) {
  const total = segments.reduce((s, seg) => s + seg.len, 0);
  let r = Math.random() * total;
  for (const seg of segments) {
    if (r <= seg.len) {
      const t = r / seg.len;
      return [seg.a[0] + (seg.b[0] - seg.a[0]) * t, seg.a[1] + (seg.b[1] - seg.a[1]) * t];
    }
    r -= seg.len;
  }
  return [0, 0];
}

function seg(a, b) {
  return { a, b, len: Math.hypot(b[0] - a[0], b[1] - a[1]) };
}

function keyTargets() {
  const pos = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    let x, y;
    const r = Math.random();
    if (r < 0.42) {
      const ang = Math.random() * Math.PI * 2;
      const rad = 0.62 + Math.random() * 0.36;
      x = Math.cos(ang) * rad;
      y = 1.35 + Math.sin(ang) * rad;
    } else if (r < 0.78) {
      x = (Math.random() - 0.5) * 0.22;
      y = 0.75 - Math.random() * 2.55;
    } else {
      const tooth = Math.random() < 0.5;
      x = 0.11 + Math.random() * (tooth ? 0.42 : 0.3);
      y = (tooth ? -1.75 : -1.35) + (Math.random() - 0.5) * 0.16;
    }
    pos[i * 3] = x * 1.15;
    pos[i * 3 + 1] = y * 1.05;
    pos[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
  }
  return pos;
}

function houseTargets() {
  const s = [
    seg([-1.15, -1.35], [1.15, -1.35]), seg([1.15, -1.35], [1.15, 0.45]),
    seg([-1.15, 0.45], [-1.15, -1.35]), seg([-1.45, 0.45], [0, 1.6]),
    seg([0, 1.6], [1.45, 0.45]), seg([-1.45, 0.45], [1.45, 0.45]),
    seg([-0.28, -1.35], [-0.28, -0.45]), seg([-0.28, -0.45], [0.28, -0.45]),
    seg([0.28, -0.45], [0.28, -1.35]),
    seg([0.5, -0.1], [0.9, -0.1]), seg([0.9, -0.1], [0.9, 0.3]),
    seg([0.9, 0.3], [0.5, 0.3]), seg([0.5, 0.3], [0.5, -0.1]),
    seg([0.55, 1.0], [0.55, 1.45]), seg([0.55, 1.45], [0.8, 1.45]), seg([0.8, 1.45], [0.8, 1.22]),
  ];
  const pos = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    const [x, y] = samplePoint(s);
    pos[i * 3] = x + (Math.random() - 0.5) * 0.06;
    pos[i * 3 + 1] = y + (Math.random() - 0.5) * 0.06;
    pos[i * 3 + 2] = (Math.random() - 0.5) * 0.4;
  }
  return pos;
}

function randomTargets(spread) {
  const pos = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    pos[i * 3] = (Math.random() - 0.5) * spread;
    pos[i * 3 + 1] = (Math.random() - 0.5) * spread;
    pos[i * 3 + 2] = (Math.random() - 0.5) * spread * 0.6;
  }
  return pos;
}

export const ParticleHero = ({ heroRef }) => {
  const mountRef = useRef(null);
  const [assembling, setAssembling] = useState(true);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.z = 5.2;

    const keyP = keyTargets();
    const houseP = houseTargets();
    const explodeP = randomTargets(22);
    const scatterP = randomTargets(12);

    const positions = new Float32Array(N * 3);
    positions.set(scatterP);
    const colors = new Float32Array(N * 3);
    const sizes = new Float32Array(N);
    const speed = new Float32Array(N);
    const phase = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      const b = 0.8 + Math.random() * 0.4;
      colors[i * 3] = GOLD.r * b;
      colors[i * 3 + 1] = GOLD.g * b;
      colors[i * 3 + 2] = GOLD.b * b;
      sizes[i] = 1.2 + Math.random() * 1.6;
      speed[i] = 0.6 + Math.random() * 0.8;
      phase[i] = Math.random() * Math.PI * 2;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));
    geo.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uPr: { value: Math.min(window.devicePixelRatio, 2) } },
      vertexShader: `
        uniform float uPr;
        attribute vec3 aColor;
        attribute float aSize;
        varying vec3 vColor;
        void main() {
          vColor = aColor;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * uPr * (5.2 / -mv.z) * 1.7;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          float d = length(gl_PointCoord - 0.5);
          if (d > 0.5) discard;
          float a = smoothstep(0.5, 0.05, d);
          gl_FragColor = vec4(vColor, a * 0.85);
        }`,
    });

    const points = new THREE.Points(geo, mat);
    scene.add(points);

    const state = { intro: 0, scroll: 0 };
    gsap.to(state, { intro: 1, duration: 1.4, ease: "power2.out", onComplete: () => setAssembling(false) });

    let st;
    if (heroRef?.current) {
      st = ScrollTrigger.create({
        trigger: heroRef.current,
        start: "top top",
        end: "+=130%",
        scrub: 0.6,
        onUpdate: (self) => { state.scroll = self.progress; },
      });
    }

    const posAttr = geo.getAttribute("position");
    const clock = new THREE.Clock();
    let raf;

    const animate = () => {
      raf = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const s = state.scroll;
      const arr = posAttr.array;
      for (let i = 0; i < N; i++) {
        const i3 = i * 3;
        let tx, ty, tz;
        if (s < 0.35) {
          const p = Math.min(1, (s / 0.35) * speed[i]);
          const e = p * p;
          tx = keyP[i3] + (explodeP[i3] - keyP[i3]) * e;
          ty = keyP[i3 + 1] + (explodeP[i3 + 1] - keyP[i3 + 1]) * e;
          tz = keyP[i3 + 2] + (explodeP[i3 + 2] - keyP[i3 + 2]) * e;
        } else {
          const p = Math.min(1, (s - 0.35) / 0.35);
          const e = p * p * (3 - 2 * p);
          tx = explodeP[i3] + (houseP[i3] - explodeP[i3]) * e;
          ty = explodeP[i3 + 1] + (houseP[i3 + 1] - explodeP[i3 + 1]) * e;
          tz = explodeP[i3 + 2] + (houseP[i3 + 2] - explodeP[i3 + 2]) * e;
        }
        const a = state.intro;
        tx = scatterP[i3] + (tx - scatterP[i3]) * a;
        ty = scatterP[i3 + 1] + (ty - scatterP[i3 + 1]) * a;
        tz = scatterP[i3 + 2] + (tz - scatterP[i3 + 2]) * a;
        const osc = 0.025;
        arr[i3] += (tx + Math.sin(t * speed[i] + phase[i]) * osc - arr[i3]) * 0.12;
        arr[i3 + 1] += (ty + Math.cos(t * speed[i] * 0.8 + phase[i]) * osc - arr[i3 + 1]) * 0.12;
        arr[i3 + 2] += (tz - arr[i3 + 2]) * 0.12;
      }
      posAttr.needsUpdate = true;
      points.rotation.y = Math.sin(t * 0.1) * 0.06;
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      if (!mount) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      if (st) st.kill();
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) mount.removeChild(renderer.domElement);
    };
  }, [heroRef]);

  return (
    <div className="absolute inset-0" data-testid="particle-canvas">
      <div ref={mountRef} className="absolute inset-0" />
      {assembling && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="mono text-[10px] tracking-[0.4em] accent shimmer">ASSEMBLING…</div>
        </div>
      )}
    </div>
  );
};
